import {apolloClient, gql} from '../graphql';
import {logAndThrowGraphQLErrors} from '../graphql/helpers';
import type {ID, Product, ProductVariant} from 'shared/src/models';

type NewProduct = Omit<Product, 'id' | 'variants'> & {
	variants: Omit<ProductVariant, 'id'>[];
};

export const createProduct = async (product: NewProduct): Promise<Product> => {
	const {data, errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation CreateProduct($newProduct: NewProduct!) {
				createProduct(newProduct: $newProduct) {
					id
					name
					variants {
						id
						description
					}
				}
			}
		`),
		variables: {
			newProduct: product,
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return {
		...data!.createProduct,
		variants: data!.createProduct.variants ?? [],
	};
};

export const createProductVariant = async (
	productId: ID,
	productVariant: Omit<ProductVariant, 'id'>,
): Promise<ProductVariant> => {
	const {data, errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation CreateProductVariant($productId: ID!, $newVariant: NewProductVariant!) {
				createProductVariant(productId: $productId, newVariant: $newVariant) {
					id
				}
			}
		`),
		variables: {
			productId,
			newVariant: productVariant,
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return {
		...productVariant,
		id: data!.createProductVariant.id,
	};
};

export const getProducts = async (): Promise<Product[]> => {
	const {data, error} = await apolloClient.query({
		query: gql(/* GraphQL */ `
			query GetProducts {
				products {
					id
					name
					variants {
						id
						description
					}
				}
			}
		`),
		fetchPolicy: 'no-cache',
	});

	if (error) {
		throw error;
	}

	return data.products.map((product) => ({
		...product,
		variants: product.variants ?? [],
	}));
};

export const updateProduct = async (product: Product): Promise<Product> => {
	const {errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation UpdateProduct($updatedProduct: UpdatedProduct!) {
				updateProduct(updatedProduct: $updatedProduct)
			}
		`),
		variables: {
			updatedProduct: product,
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return product;
};

export const updateProductVariant = async (
	productVariant: ProductVariant,
): Promise<ProductVariant> => {
	const {errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation UpdateVariant($updatedVariant: UpdatedProductVariant!) {
				updateProductVariant(updatedVariant: $updatedVariant)
			}
		`),
		variables: {
			updatedVariant: productVariant,
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return productVariant;
};
